'use client';

import { Check } from 'lucide-react';
import { useRef, useState } from 'react';

import type { QuizOption, QuizQuestion } from '@/entities/interview-task';
import { ChatMarkdown } from '@/shared/ui/markdown';

type InlineQuizProps = {
	question: QuizQuestion;
	isLocked: boolean;
	/** Кнопка «Проверить» доступна только когда диалог в idle. */
	actionsDisabled?: boolean;
	onAttempt: (answerText: string) => void;
	onCorrect: (
		answerText: string,
		ctx: { optionIds: string[]; answerText: string },
	) => void;
	onWrong: (hintText: string) => void;
};

export function InlineQuiz({
	question,
	isLocked,
	actionsDisabled = false,
	onAttempt,
	onCorrect,
	onWrong,
}: InlineQuizProps) {
	const [single, setSingle] = useState<number | null>(null);
	const [multi, setMulti] = useState<Set<number>>(() => new Set());
	const [inputVal, setInputVal] = useState('');
	const [wrongIdx, setWrongIdx] = useState<number | null>(null);
	const [wrongMulti, setWrongMulti] = useState(false);
	const [wrongInput, setWrongInput] = useState(false);
	const checkingRef = useRef(false);

	const text = question.question;
	const hint = question.hint;

	const canSubmit =
		(question.type === 'single' && single !== null) ||
		(question.type === 'multi' && multi.size > 0) ||
		(question.type === 'input' && inputVal.trim().length > 0);

	const check = () => {
		if (isLocked || actionsDisabled || !canSubmit || checkingRef.current)
			return;
		checkingRef.current = true;

		let answerText = '';
		let ok = false;
		let hintText = hint?.text || 'Попробуй ещё раз.';
		const selectedOptions: QuizOption[] = [];

		if (question.type === 'single' && single !== null) {
			const opt = question.options[single];
			answerText = opt?.text ?? '';
			if (opt) selectedOptions.push(opt);
			// Если correct присутствует (hardcoded fixture) — проверяем локально.
			// Иначе верим бэку: считаем попытку корректной и предоставляем optionId.
			ok = opt ? (opt.correct ?? true) : false;
		} else if (question.type === 'multi') {
			const picked = [...multi].map((i) => question.options[i]);
			selectedOptions.push(...picked);
			answerText = picked.map((o) => o.text).join('\n');
			const hasLocalCorrect = question.options.some(
				(o) => o.correct !== undefined,
			);
			if (hasLocalCorrect) {
				const correctIdxs = new Set(
					question.options
						.map((o, i) => (o.correct ? i : null))
						.filter((x): x is number => x !== null),
				);
				ok =
					correctIdxs.size === multi.size &&
					[...correctIdxs].every((i) => multi.has(i));
				if (!ok) {
					hintText = hint?.text || 'Не все варианты верны. Попробуй ещё раз.';
				}
			} else {
				// Бэк проверит сам — локально считаем попытку успешной.
				ok = picked.length > 0;
			}
		} else if (question.type === 'input') {
			answerText = inputVal.trim();
			ok = question.answers.some(
				(a) => a.trim().toLowerCase() === answerText.toLowerCase(),
			);
		}

		onAttempt(answerText);

		if (ok) {
			setWrongIdx(null);
			setWrongMulti(false);
			setWrongInput(false);
			const optionIds = selectedOptions
				.map((o) => o.id)
				.filter((id): id is string => typeof id === 'string');
			onCorrect(answerText, { optionIds, answerText });
		} else {
			if (question.type === 'single') setWrongIdx(single);
			else if (question.type === 'multi') setWrongMulti(true);
			else if (question.type === 'input') setWrongInput(true);
			onWrong(hintText);
			checkingRef.current = false;
		}
	};

	return (
		<div className="flex h-full flex-col">
			<div className="border-b border-gray-4 px-6 py-5 text-[15px] text-black [&>*:first-child]:mt-0">
				<ChatMarkdown>{text}</ChatMarkdown>
			</div>

			{question.type === 'single' && (
				<div className="flex-1 divide-y divide-gray-4 overflow-y-auto">
					{question.options.map((opt, i) => {
						const isSelected = single === i;
						const isCorrect = isLocked && opt.correct;
						const isWrong = wrongIdx === i;
						return (
							<button
								type="button"
								key={i}
								onClick={() => {
									if (!isLocked) {
										setSingle(i);
										setWrongIdx(null);
									}
								}}
								disabled={isLocked}
								className={`flex w-full items-center gap-3 px-6 py-4 text-left text-sm transition-colors ${isLocked ? 'cursor-default' : 'cursor-pointer hover:bg-gray-5'} ${isCorrect ? 'bg-green/10' : ''} ${isWrong ? 'bg-red-light' : ''} `}
							>
								<span
									className={`flex size-6 shrink-0 items-center justify-center rounded-md text-xs font-semibold ${isCorrect ? 'bg-green text-white' : ''} ${isWrong ? 'bg-red text-white' : ''} ${!isCorrect && !isWrong && isSelected ? 'bg-black text-white' : ''} ${!isCorrect && !isWrong && !isSelected ? 'bg-gray-5 text-gray-3' : ''} `}
								>
									{isCorrect ? <Check size={12} strokeWidth={3} /> : i + 1}
								</span>
								<span
									className={
										isCorrect
											? 'text-green'
											: isWrong
												? 'text-red-hover'
												: isSelected
													? 'text-black'
													: 'text-gray-3'
									}
								>
									{opt.text}
								</span>
							</button>
						);
					})}
				</div>
			)}

			{question.type === 'multi' && (
				<div className="flex-1 divide-y divide-gray-4 overflow-y-auto">
					{question.options.map((opt, i) => {
						const isSelected = multi.has(i);
						const isCorrect = isLocked && opt.correct;
						const isWrong = wrongMulti && isSelected;
						return (
							<button
								type="button"
								key={i}
								onClick={() => {
									if (isLocked) return;
									setWrongMulti(false);
									setMulti((prev) => {
										const next = new Set(prev);
										if (next.has(i)) next.delete(i);
										else next.add(i);
										return next;
									});
								}}
								disabled={isLocked}
								className={`flex w-full items-center gap-3 px-6 py-4 text-left text-sm transition-colors ${isLocked ? 'cursor-default' : 'cursor-pointer hover:bg-gray-5'} ${isCorrect ? 'bg-green/10' : ''} ${isWrong ? 'bg-red-light' : ''} `}
							>
								<span
									className={`flex size-6 shrink-0 items-center justify-center rounded-md border text-xs font-semibold ${isCorrect ? 'border-green bg-green text-white' : ''} ${isWrong ? 'border-red bg-red text-white' : ''} ${!isCorrect && !isWrong && isSelected ? 'border-black bg-black text-white' : ''} ${!isCorrect && !isWrong && !isSelected ? 'border-gray-4 bg-gray-5 text-gray-3' : ''} `}
								>
									{isCorrect ? <Check size={12} strokeWidth={3} /> : i + 1}
								</span>
								<span
									className={
										isCorrect
											? 'text-green'
											: isWrong
												? 'text-red-hover'
												: isSelected
													? 'text-black'
													: 'text-gray-3'
									}
								>
									{opt.text}
								</span>
							</button>
						);
					})}
				</div>
			)}

			{question.type === 'input' && (
				<div className="flex-1 px-6 py-5">
					<input
						type="text"
						value={inputVal}
						onChange={(e) => {
							setInputVal(e.target.value);
							setWrongInput(false);
						}}
						onKeyDown={(e) => {
							if (e.key === 'Enter') check();
						}}
						disabled={isLocked}
						placeholder="Твой ответ..."
						className={`w-full rounded-lg border px-3 py-2 text-sm outline-none transition-colors ${
							isLocked
								? 'cursor-default border-green bg-green/10 text-green'
								: wrongInput
									? 'border-red bg-red-light text-red-hover placeholder:text-red'
									: 'border-gray-4 bg-white text-black placeholder:text-gray-4 focus:border-gray-3'
						} `}
					/>
				</div>
			)}

			{!isLocked && (
				<div className="flex shrink-0 justify-end border-t border-gray-4 px-6 py-4">
					<button
						type="button"
						onClick={check}
						disabled={!canSubmit || actionsDisabled}
						className="h-9 cursor-pointer rounded-lg bg-green px-5 text-sm font-medium text-white transition-colors hover:bg-green-300 disabled:cursor-not-allowed disabled:opacity-40"
					>
						Проверить
					</button>
				</div>
			)}
		</div>
	);
}
